import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { changeLanguage, getCurrentLanguage } from '../i18n';

export type SupportedLanguage = 'en' | 'fr';

interface LanguageState {
  currentLanguage: SupportedLanguage;
  isChanging: boolean;
  error: string | null;
}

const initialState: LanguageState = {
  currentLanguage: 'en',
  isChanging: false,
  error: null,
};

// Async thunk to change language
export const changeAppLanguage = createAsyncThunk(
  'language/changeAppLanguage',
  async (language: SupportedLanguage, { rejectWithValue }) => {
    try {
      await changeLanguage(language);
      return language;
    } catch (error) {
      console.error('Failed to change language:', error);
      return rejectWithValue('Failed to change language');
    }
  }
);

// Async thunk to initialize language from i18n
export const initializeLanguage = createAsyncThunk(
  'language/initializeLanguage',
  async (_, { rejectWithValue }) => {
    try {
      // Wait for i18n to be initialized
      await new Promise(resolve => setTimeout(resolve, 100));
      const currentLang = getCurrentLanguage();
      return currentLang;
    } catch (error) {
      console.error('Failed to initialize language:', error);
      return rejectWithValue('Failed to initialize language');
    }
  }
);

const languageSlice = createSlice({
  name: 'language',
  initialState,
  reducers: {
    setLanguage: (state, action: PayloadAction<SupportedLanguage>) => {
      state.currentLanguage = action.payload;
      state.error = null;
    },
    clearLanguageError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Change language
      .addCase(changeAppLanguage.pending, (state) => {
        state.isChanging = true;
        state.error = null;
      })
      .addCase(changeAppLanguage.fulfilled, (state, action) => {
        state.isChanging = false;
        state.currentLanguage = action.payload;
        state.error = null;
      })
      .addCase(changeAppLanguage.rejected, (state, action) => {
        state.isChanging = false;
        state.error = action.payload as string;
      })
      // Initialize language
      .addCase(initializeLanguage.pending, (state) => {
        state.isChanging = true;
        state.error = null;
      })
      .addCase(initializeLanguage.fulfilled, (state, action) => {
        state.isChanging = false;
        state.currentLanguage = action.payload;
        state.error = null;
      })
      .addCase(initializeLanguage.rejected, (state, action) => {
        state.isChanging = false;
        state.error = action.payload as string;
      });
  },
});

export const { setLanguage, clearLanguageError } = languageSlice.actions;
export default languageSlice.reducer;

// Selectors
export const selectCurrentLanguage = (state: { language: LanguageState }) => state.language.currentLanguage;
export const selectIsChangingLanguage = (state: { language: LanguageState }) => state.language.isChanging;
export const selectLanguageError = (state: { language: LanguageState }) => state.language.error;